/**
 * Lightweight JWT helpers for the PWA. The API does the
 * cryptographic verification on every request — these
 * helpers only read the public payload segment for client-
 * side checks like "does the token's `sub` claim match the
 * stored user object's `_id`."
 *
 * No signature verification, ever. A forged token will fail
 * the API's Bearer check on the next request anyway; the
 * client doesn't need to (and can't safely) verify
 * signatures from the browser.
 *
 * Two consumers today:
 *   - NonDriverBanner — desync warning surface that compares
 *     the stored token's sub against the stored user's _id
 *     and renders a hard-warning recovery card on mismatch.
 *   - api.ts afterResponse hook — defensive validation when a
 *     refresh-token rotation issues a new access token, to
 *     catch the case where the refresh cookie is for a
 *     different user than the one currently in the auth store
 *     (cookie-leak edge case prior to the server-side logout
 *     endpoint landing).
 *
 * Both consumers import from here so the decode logic lives
 * in one place — fewer surfaces to keep in sync if the JWT
 * shape ever changes.
 */

/**
 * Decode a JWT's payload segment to extract its `sub` claim.
 * Returns null when the token isn't a well-formed JWT —
 * intentional, so callers can distinguish "no useful info"
 * from "different sub than expected" without a false-trigger
 * on malformed tokens.
 */
export function decodeJwtSub(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadSegment = parts[1];
    if (!payloadSegment) return null;
    // base64url → base64 (URL-safe `-` and `_` map back to
    // `+` and `/`; padding is implicit in JWT and may be
    // missing — pad to a multiple of 4 before decoding).
    const b64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json: unknown = JSON.parse(atob(padded));
    if (json && typeof json === 'object' && 'sub' in json) {
      const sub = (json as { sub: unknown }).sub;
      return typeof sub === 'string' ? sub : null;
    }
    return null;
  } catch {
    return null;
  }
}
